/**
 * SectionGuard — restricts access to sections based on per-user access config.
 *
 * Wraps page content and shows an access-denied message when the current user's
 * effective access level for the given section is "none".
 *
 * Usage:
 *   <SectionGuard section="campaigns">
 *     <CampaignsList />
 *   </SectionGuard>
 */

import type { Section } from '@/api/access'
import { SECTION_LABELS } from '@/api/access'
import { Button } from '@/components/ui/button'
import { useCanAccess } from '@/hooks/use-access'
import { ShieldOff } from 'lucide-react'
import { useNavigate } from 'react-router'

interface SectionGuardProps {
  /** The section this guard protects. */
  section: Section
  /** Content to render when access is granted. */
  children: React.ReactNode
  /** Optional fallback to render instead of default "Access Denied" screen. */
  fallback?: React.ReactNode
}

export function SectionGuard({ section, children, fallback }: SectionGuardProps) {
  const hasAccess = useCanAccess(section)
  const navigate = useNavigate()

  if (hasAccess) {
    return <>{children}</>
  }

  // Custom fallback
  if (fallback) {
    return <>{fallback}</>
  }

  // Default "Access Denied" page
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
      <div className="rounded-full bg-muted p-4">
        <ShieldOff className="h-10 w-10 text-muted-foreground" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground max-w-md">
          You do not have permission to access the{' '}
          <span className="font-medium">{SECTION_LABELS[section]}</span> section.
          Contact your team admin to request access.
        </p>
      </div>
      <Button variant="outline" onClick={() => navigate('/')}>
        Go to Dashboard
      </Button>
    </div>
  )
}
